import SkaterSvg from '@/assets/icons/skater.svg?react';
import { AnimatePresence } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import { LuLayoutGrid } from 'react-icons/lu';
import { MdOutlineSurfing, MdSurfing } from 'react-icons/md';
import { toast } from 'react-toastify';
import { ReactSVG } from 'react-svg';
import { Calendar } from '../../components/Calendar';
import { CardImage } from '../../components/CardImage';
import { ItemAddToCart } from '../../components/ItemAddToCart';
import { ModalLoginNotice } from '../../components/ModalLoginNotice';
import { Loading } from '../../components/SurfingLoading';
import { allTypeListCategory } from '../../consts/typeListConst';
import {
    canAccessStore,
    STORE_RESERVATION_REQUIRED_MESSAGE,
} from '../../helpers/storeAccess';
import { useReservationStore } from '../../store/reservation/reservationStore';
import { useToken } from '../../store/auth/tokenStore';
import { useLogicHome } from './logic/useHomeLogic';
import { useBannerStore } from '../../store/ui/bannerStore';
import { featureFlags } from '../../config/featureFlags';
import PopupAgostoBanner from '@/assets/banner/popup-agosto.png';
import {
    Button,
    ButtonsWrapper,
    Container,
    SVGWrap,
    ServiceWrap,
    Title,
    TitleWrapper,
    TopImagesWrapper,
} from './styles';

const currentDate = new Date();

export const Home: React.FC = () => {
    const {
        dataShopItems,
        isPendingShopItems,
        selectedTypeProductId,
        dataTypesList,
        isPendingTypeList,
        currentCartAddValue,
        setCurrentCartAddValue,
        handleAddToCart,
        handleOpenPark,
        handleOpenStore,
        setSelectedTypeProductId,
    } = useLogicHome();

    const conditionToLoadingPending = isPendingShopItems || isPendingTypeList;
    const { token } = useToken();
    const activeReservation = useReservationStore(state => state.activeReservation);
    const [hasSeenLoginNotice, setHasSeenLoginNotice] = useState(false);
    const [hasSeenBanner, setHasSeenBanner] = useState(() => {
        try {
            return sessionStorage.getItem('banner:login:seen') === '1';
        } catch {
            return false;
        }
    });

    const [bannerWasShown, setBannerWasShown] = useState(false);

    // Mostra o banner na primeira entrada da sessão (antes do modal de login)
    useEffect(() => {
        if (!conditionToLoadingPending && !token && !hasSeenBanner && featureFlags.showLoginBanner) {
            useBannerStore.getState().showBanner({
                imageSrc: PopupAgostoBanner,
                alt: 'Banner de agosto',
            });
            setBannerWasShown(true);
        }
    }, [conditionToLoadingPending, token, hasSeenBanner]);

    // Monitora quando o banner é fechado
    const { isVisible: bannerVisible } = useBannerStore();
    useEffect(() => {
        // Se o banner foi mostrado e agora foi fechado, marca como visto
        if (bannerWasShown && !bannerVisible && !hasSeenBanner) {
            setHasSeenBanner(true);
            try {
                sessionStorage.setItem('banner:login:seen', '1');
            } catch {
                /* ignore */
            }
        }
    }, [bannerVisible, bannerWasShown, hasSeenBanner]);

    const handleOpenStoreWithGuard = () => {
        if (!canAccessStore({ activeReservation })) {
            toast.warning(STORE_RESERVATION_REQUIRED_MESSAGE);
            return;
        }
        handleOpenStore(true);
    };

    return (
        <>
            {conditionToLoadingPending && <Loading />}
            {!conditionToLoadingPending && !token && (hasSeenBanner || !featureFlags.showLoginBanner) && (
                <ModalLoginNotice
                    isVisible={!hasSeenLoginNotice}
                    setVisible={(d: boolean) => setHasSeenLoginNotice(!d)}
                />
            )}
            <Container>
                <TitleWrapper>
                    <Title>Tickets</Title>
                </TitleWrapper>
                <TopImagesWrapper>
                    <CardImage
                        text="Visitar o parque"
                        icon={<SkaterSvg />}
                        src="/images/park.png"
                        onClick={() => handleOpenPark(true)}
                        description="Descubra nossa infraestrutura completa para momentos de lazer, entretenimento, esportes e gastronomia!"
                        className="park"
                    />
                    <CardImage
                        text="Surfar"
                        icon={<MdOutlineSurfing />}
                        src="/images/surf.new.jpg"
                        onClick={handleOpenStoreWithGuard}
                        description="Do iniciante ao avançado, ondas perfeitas para você evoluir e se divertir. Aqui não tem flat! Garanta sua sessão agora!"
                    />
                </TopImagesWrapper>
                <TitleWrapper id="services-header">
                    <Title>Serviços</Title>
                </TitleWrapper>
                <ButtonsWrapper>
                    {dataTypesList?.map(({ id, icon, name }) => {
                        return name === allTypeListCategory.name ? (
                            <Button
                                key={'all'}
                                onClick={() =>
                                    setSelectedTypeProductId(undefined)
                                }
                                $isSelected={
                                    selectedTypeProductId === undefined
                                }
                            >
                                <SVGWrap>
                                    <LuLayoutGrid />
                                </SVGWrap>
                                Ver tudo
                            </Button>
                        ) : (
                            <Button
                                key={id}
                                onClick={() => setSelectedTypeProductId(id)}
                                $isSelected={selectedTypeProductId === id}
                            >
                                {icon && (
                                    <SVGWrap>
                                        <ReactSVG src={icon} />
                                    </SVGWrap>
                                )}
                                {name}
                            </Button>
                        );
                    })}
                </ButtonsWrapper>

                <ServiceWrap>
                    {dataShopItems
                        ?.filter(item => Boolean(item.information2))
                        ?.map(item => (
                            <ItemAddToCart
                                key={item.id}
                                id={String(item.id)}
                                imageUrl={item.image}
                                IconComponent={MdSurfing}
                                title={item.exhibitionName}
                                description={item.description}
                                handleAddToCart={setCurrentCartAddValue}
                                value={item.value}
                                information1={item.information1}
                                date={currentDate}
                                item={item}
                            />
                        ))}
                </ServiceWrap>
            </Container>

            <AnimatePresence>
                {currentCartAddValue && (
                    <Calendar
                        onClose={date => {
                            if (date) {
                                handleAddToCart({
                                    ...currentCartAddValue,
                                    date: date,
                                });
                                setCurrentCartAddValue(null);

                                return;
                            }
                            setCurrentCartAddValue(null);
                        }}
                    />
                )}
            </AnimatePresence>
        </>
    );
};
